import { FormTitle } from "@/components/Forms/components/MonolithFormComponents";
import OfficeSelector from "@/components/Forms/components/Selectors/OfficeSelector";
import { usePermissions } from "@/hooks/usePermissions";
import { useForm } from "@mantine/form";
import { Button, Grid, TextInput } from "@monolith-forensics/monolith-ui";
import { useSnackbar, VariantType } from "notistack";
import { FC } from "react";
import { User } from "../../types/Users";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";

import LoggingAPI from "@/api/logging";
import { userApi } from "@/api";
import { Office } from "@/Features/Offices";
import { currencyRegex } from "@/components/Forms/utils/zod/regexHelpers";

interface UserDetailsBlockProps {
  userData: User;
  offices: Office[];
  readOnly?: boolean;
  onSubmit: () => void;
}

// Define the allowed fields schema
export const userUpdateSchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  title: z.string().optional(),
  office_id: z.number().or(z.string()),
  price_per_hour: z
    .string()
    .regex(currencyRegex, "Invalid - please enter a cost number")
    .or(z.number())
    .nullable()
    .optional(),
});

export type UserUpdateData = z.infer<typeof userUpdateSchema>;

const UserDetailsBlock: FC<UserDetailsBlockProps> = ({
  userData,
  offices,
  readOnly,
  onSubmit,
}) => {
  const form = useForm({
    mode: "controlled",
    initialValues: userData || {},
  });

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(userUpdateSchema)(formData);
    if (validationResult?.hasErrors) {
      form.setErrors(validationResult?.errors);
      return;
    }

    try {
      const userUpdateData = {
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        title: formData.title || null,
        office_id: formData?.office_id || null,
        price_per_hour: formData.price_per_hour || null,
      };

      await userApi.updateUser(userData.user_id, userUpdateData);

      enqueueSnackbar(`User updated successfully`, {
        variant: "success" as VariantType,
      });
      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `User Updated: ${
          formData.first_name + " " + formData.last_name
        }`,
        event: "user:update",
        object_type: "user",
      });

      form.resetTouched();
      onSubmit();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`An error occurred while updating user`, {
        variant: "error" as VariantType,
      });
    }
  };

  return (
    <div
      style={{
        margin: "20px 0",
      }}
    >
      <div style={{ marginBottom: "20px" }}>
        <FormTitle>User Details</FormTitle>
      </div>
      <Grid col={2}>
        <TextInput
          size="sm"
          variant="outlined"
          label="First Name"
          placeholder={readOnly ? "" : "Add User's First Name"}
          description="User's First Name"
          disabled={readOnly}
          key={form.key("first_name")}
          {...form.getInputProps("first_name")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Last Name"
          placeholder={readOnly ? "" : "Add User's Last Name"}
          description="User's Last Name"
          disabled={readOnly}
          key={form.key("last_name")}
          {...form.getInputProps("last_name")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Title"
          placeholder={readOnly ? "" : "Add User's Title"}
          description="User's title"
          disabled={readOnly}
          key={form.key("title")}
          {...form.getInputProps("title")}
        />
        {readOnly ? (
          <TextInput
            size="sm"
            variant="outlined"
            label="Office"
            disabled={readOnly}
            key={form.key("office_name")}
            {...form.getInputProps("office_name")}
          />
        ) : (
          <OfficeSelector
            key={form.key("office_id")}
            {...form.getInputProps("office_id")}
          />
        )}
        <TextInput
          size="sm"
          variant="outlined"
          label="Hourly Rate"
          type="number"
          min="0"
          placeholder="0"
          description="User's billable rate"
          disabled={readOnly}
          key={form.key("price_per_hour")}
          {...form.getInputProps("price_per_hour")}
        />
      </Grid>
      {form?.isTouched() && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "15px",
          }}
        >
          <Button
            size="xs"
            color="primary"
            variant="light"
            onClick={handleSubmit}
          >
            Save User Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default UserDetailsBlock;
